import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { api } from "../lib/api";
import { ChevronLeft, Users, Bot, LogIn, ShieldAlert, Search, ChevronRight, ChevronUp, ChevronDown, UserPlus, Copy, Trash2, Pencil, Plus } from "lucide-react";
import { Button } from "../components/Button";
import { VerbrauchModal } from "../components/VerbrauchModal";
import styles from "./AdminPage.module.css";

// ── Helpers ──

function formatDe(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Types ──

interface Stats {
  users: number;
  bots: number;
  logins24h: number;
  failedLogins24h: number;
}

interface OrgMembership {
  orgId: string;
  orgName: string;
  role: string; // owner | admin | moderator | member
  joinedAt: string; // ISO
}

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isBot: boolean;
  isAdmin: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  orgRole?: string | null;
  orgs: OrgMembership[];
}

type SortKey =
  | "displayName"
  | "email"
  | "organisation"
  | "gruppe"
  | "status"
  | "lastLoginAt"
  | "createdAt";
type SortDir = "asc" | "desc";

interface AuditEntry {
  id: string;
  userId: string | null;
  displayName: string | null;
  action: string;
  ip: string | null;
  userAgent: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Invitation {
  id: string;
  token: string;
  email: string | null;
  maxUses: number;
  useCount: number;
  expiresAt: string;
  createdAt: string;
  revokedAt: string | null;
  isActive: boolean;
  link: string;
}

// ── Main Page ──

export function AdminPage({ onBack }: { onBack: () => void }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"dashboard" | "users" | "organisationen" | "invitations" | "audit">("dashboard");
  const isOwner = user?.orgRole === "owner";

  const orgRole = user?.orgRole;
  if (!orgRole || (orgRole !== "owner" && orgRole !== "admin")) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onBack}>
          <ChevronLeft size={18} /> Zurück
        </button>
        <h2 className={styles.title}>Admin-Panel</h2>
      </div>

      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === "dashboard" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("dashboard")}
        >
          Dashboard
        </button>
        <button
          className={`${styles.tab} ${activeTab === "users" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Benutzer
        </button>
        {isOwner && (
          <button
            className={`${styles.tab} ${activeTab === "organisationen" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("organisationen")}
          >
            Organisationen
          </button>
        )}
        <button
          className={`${styles.tab} ${activeTab === "invitations" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("invitations")}
        >
          Einladungen
        </button>
        <button
          className={`${styles.tab} ${activeTab === "audit" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("audit")}
        >
          Audit-Log
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "users" && <UsersTab />}
        {activeTab === "organisationen" && isOwner && <OrganisationenTab />}
        {activeTab === "invitations" && <InvitationsTab />}
        {activeTab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

// ── Dashboard Tab ──

function DashboardTab() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get<Stats>("/admin/stats")
      .then(setStats)
      .catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className={styles.loadingHint}>Wird geladen…</div>;
  }

  if (error) {
    return <div className={styles.errorMsg}>{error}</div>;
  }

  if (!stats) return null;

  return (
    <div className={styles.statsGrid}>
      <StatCard
        icon={<Users size={24} />}
        label="Aktive Benutzer"
        value={stats.users}
        color="brand"
      />
      <StatCard
        icon={<Bot size={24} />}
        label="Bots"
        value={stats.bots}
        color="accent"
      />
      <StatCard
        icon={<LogIn size={24} />}
        label="Logins (24h)"
        value={stats.logins24h}
        color="success"
      />
      <StatCard
        icon={<ShieldAlert size={24} />}
        label="Fehlgeschlagene Logins (24h)"
        value={stats.failedLogins24h}
        color="error"
      />
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: "brand" | "accent" | "success" | "error";
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  return (
    <div className={`${styles.statCard} ${styles[`statCard_${color}`]}`}>
      <div className={styles.statIcon}>{icon}</div>
      <div className={styles.statValue}>{value.toLocaleString("de-DE")}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

// ── Users Tab ──

function UsersTab() {
  const { user: currentUser } = useAuth();
  const [userList, setUserList] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedOrgs, setExpandedOrgs] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const isOwner = currentUser?.orgRole === "owner";

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedUserList = useMemo(() => {
    const roleRank: Record<string, number> = { owner: 4, admin: 3, moderator: 2, member: 1 };
    const dir = sortDir === "asc" ? 1 : -1;

    const valueOf = (u: AdminUser): string | number => {
      switch (sortKey) {
        case "displayName":
          return u.displayName.toLowerCase();
        case "email":
          return u.email.toLowerCase();
        case "organisation":
          return (u.orgs[0]?.orgName ?? "").toLowerCase();
        case "gruppe":
          if (u.isBot) return -1;
          return roleRank[u.orgRole ?? ""] ?? 0;
        case "status":
          return u.isActive ? 1 : 0;
        case "lastLoginAt":
          return u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : 0;
        case "createdAt":
          return new Date(u.createdAt).getTime();
      }
    };

    const arr = [...userList];
    arr.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [userList, sortKey, sortDir]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (includeInactive) params.set("includeInactive", "true");
    api
      .get<{ users: AdminUser[] }>(`/admin/users?${params.toString()}`)
      .then((d) => setUserList(d.users.map((u) => ({ ...u, orgs: u.orgs ?? [] }))))
      .catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [query, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const groupLabel = (u: AdminUser): string => {
    if (u.isBot) return "Bot";
    if (u.orgRole === "owner") return "Owner";
    if (u.orgRole === "admin") return "Admin";
    if (u.orgRole === "moderator") return "Moderator";
    if (u.orgRole === "member") return "Mitglied";
    return "Kein Zugang";
  };

  const groupClass = (u: AdminUser): string => {
    if (u.isBot) return styles.roleBot;
    if (u.orgRole === "owner") return styles.roleAdmin;
    if (u.orgRole === "admin") return styles.roleAdmin;
    if (u.orgRole === "moderator") return styles.roleModerator ?? styles.roleUser;
    return styles.roleUser;
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteConfirm.id}`);
      setUserList((prev) => prev.filter((u) => u.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Löschen");
    } finally {
      setDeleting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setChangingRole(userId);
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      setUserList((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, orgRole: newRole, isAdmin: newRole === "admin" } : u)),
      );
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Ändern der Rolle");
    } finally {
      setChangingRole(null);
    }
  };

  const orgRoleLabel = (role: string): string => {
    if (role === "owner") return "Owner";
    if (role === "admin") return "Admin";
    if (role === "moderator") return "Moderator";
    if (role === "member") return "Mitglied";
    return role;
  };

  const renderSortIcon = (active: boolean, dir: SortDir) => {
    if (!active) return <ChevronUp size={12} className={styles.sortIconIdle} />;
    return dir === "asc" ? (
      <ChevronUp size={12} className={styles.sortIconActive} />
    ) : (
      <ChevronDown size={12} className={styles.sortIconActive} />
    );
  };

  return (
    <div className={styles.tabSection}>
      <div className={styles.filterRow}>
        <div className={styles.searchWrapper}>
          <Search size={14} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Name suchen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Inaktive einbeziehen
        </label>
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {loading ? (
        <div className={styles.loadingHint}>Wird geladen…</div>
      ) : userList.length === 0 ? (
        <div className={styles.emptyHint}>Keine Benutzer gefunden.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.sortHeader} onClick={() => toggleSort("displayName")}>
                  <span>Name</span>{renderSortIcon(sortKey === "displayName", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("email")}>
                  <span>E-Mail</span>{renderSortIcon(sortKey === "email", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("organisation")}>
                  <span>Organisation</span>{renderSortIcon(sortKey === "organisation", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("gruppe")}>
                  <span>Gruppe</span>{renderSortIcon(sortKey === "gruppe", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("status")}>
                  <span>Status</span>{renderSortIcon(sortKey === "status", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("lastLoginAt")}>
                  <span>Letzter Login</span>{renderSortIcon(sortKey === "lastLoginAt", sortDir)}
                </th>
                <th className={styles.sortHeader} onClick={() => toggleSort("createdAt")}>
                  <span>Erstellt</span>{renderSortIcon(sortKey === "createdAt", sortDir)}
                </th>
                {isOwner && <th></th>}
              </tr>
            </thead>
            <tbody>
              {sortedUserList.map((u) => (
                <tr key={u.id}>
                  <td className={styles.tdName}>{u.displayName}</td>
                  <td className={styles.tdMuted}>{u.email}</td>
                  <td className={styles.tdOrg}>
                    {u.orgs.length === 0 ? (
                      <span className={styles.tdMuted}>—</span>
                    ) : (
                      <span className={styles.orgCell}>
                        <span className={styles.orgPrimary}>{u.orgs[0].orgName}</span>
                        {u.orgs.length > 1 && (
                          <button
                            type="button"
                            className={styles.orgMoreChip}
                            title={u.orgs
                              .slice(1)
                              .map(
                                (m) =>
                                  `${m.orgName} (${orgRoleLabel(m.role)}, beigetreten ${formatDe(m.joinedAt)})`,
                              )
                              .join("\n")}
                            onClick={() =>
                              setExpandedOrgs((cur) => (cur === u.id ? null : u.id))
                            }
                          >
                            +{u.orgs.length - 1}
                          </button>
                        )}
                        {expandedOrgs === u.id && u.orgs.length > 1 && (
                          <ul className={styles.orgExpandedList}>
                            {u.orgs.slice(1).map((m) => (
                              <li key={m.orgId}>
                                <strong>{m.orgName}</strong>{" "}
                                <span className={styles.tdMuted}>
                                  ({orgRoleLabel(m.role)}, beigetreten {formatDe(m.joinedAt)})
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    {isOwner && !u.isBot && u.orgRole !== "owner" && u.id !== currentUser?.id ? (
                      <select
                        className={styles.roleSelect}
                        value={u.orgRole ?? "member"}
                        disabled={changingRole === u.id}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      >
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="member">Mitglied</option>
                      </select>
                    ) : (
                      <span className={`${styles.badge} ${groupClass(u)}`}>{groupLabel(u)}</span>
                    )}
                  </td>
                  <td>
                    <span className={`${styles.badge} ${u.isActive ? styles.statusActive : styles.statusInactive}`}>
                      {u.isActive ? "Aktiv" : "Inaktiv"}
                    </span>
                  </td>
                  <td className={styles.tdMuted}>{formatDe(u.lastLoginAt)}</td>
                  <td className={styles.tdMuted}>{formatDe(u.createdAt)}</td>
                  {isOwner && (
                    <td>
                      {!u.isBot && u.orgRole !== "owner" && u.id !== currentUser?.id && (
                        <button
                          className={styles.deleteBtn}
                          title="Benutzer löschen"
                          onClick={() => setDeleteConfirm(u)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3>Benutzer löschen</h3>
            <p>
              <strong>{deleteConfirm.displayName}</strong> ({deleteConfirm.email}) wirklich löschen?
              Der Benutzer wird aus allen Chats entfernt und seine Nachrichten anonymisiert.
            </p>
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Abbrechen
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Wird gelöscht…" : "Endgültig löschen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Organisationen Tab (owner only) ──

interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  /** Bot-Vorgänge pro Monat; null = kein Kontingent vereinbart (KOL-94) */
  vorgaengeQuota: number | null;
  /** Wer den Verbrauchsbalken sieht: alle Mitglieder oder nur Owner/Admins */
  usageVisibility: "members" | "admins";
  /** Vorgänge des laufenden Monats (KOL-112) */
  verbrauchLaufend: number;
}

interface OrgMember {
  userId: string;
  role: string;
  joinedAt: string;
  displayName: string;
  email: string;
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const orgRoleLabelDe = (r: string) =>
  ({ owner: "Owner", admin: "Admin", moderator: "Moderator", member: "Mitglied" })[r] ?? r;

function OrganisationenTab() {
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<AdminOrg | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminOrg | null>(null);
  const [managing, setManaging] = useState<AdminOrg | null>(null);
  // KOL-94: Kontingent + Sichtbarkeit — nur Plattform-Admins, der Server prüft das.
  const [quotaTarget, setQuotaTarget] = useState<AdminOrg | null>(null);
  const [quotaValue, setQuotaValue] = useState("");
  const [visibilityValue, setVisibilityValue] = useState<"members" | "admins">("members");
  const [savingQuota, setSavingQuota] = useState(false);
  // KOL-112: Verbrauch ansehen — getrennt vom Bearbeiten des Kontingents.
  const [verbrauchTarget, setVerbrauchTarget] = useState<AdminOrg | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get<{ organizations: AdminOrg[] }>("/admin/orgs")
      .then((d) => setOrgs(d.organizations))
      .catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const handleCreate = async () => {
    const name = newName.trim();
    const slug = slugify(name);
    if (slug.length < 3) {
      setError("Name zu kurz (Slug braucht mind. 3 Zeichen).");
      return;
    }
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/orgs", { name, slug });
      setNewName("");
      load();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Anlegen");
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async () => {
    if (!renaming) return;
    const name = renameValue.trim();
    if (!name) return;
    try {
      await api.patch(`/admin/orgs/${renaming.id}`, { name });
      setRenaming(null);
      load();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Umbenennen");
    }
  };

  const openQuota = (o: AdminOrg) => {
    setQuotaTarget(o);
    setQuotaValue(o.vorgaengeQuota === null ? "" : String(o.vorgaengeQuota));
    setVisibilityValue(o.usageVisibility);
    setError("");
  };

  const handleSaveQuota = async () => {
    if (!quotaTarget) return;
    const trimmed = quotaValue.trim();
    // Leeres Feld heißt „kein Kontingent" — ein gültiger Zustand, kein Fehler.
    const quota = trimmed === "" ? null : Number(trimmed);
    if (quota !== null && (!Number.isInteger(quota) || quota <= 0)) {
      setError("Kontingent muss eine positive ganze Zahl sein (oder leer für unbegrenzt).");
      return;
    }
    setSavingQuota(true);
    setError("");
    try {
      await api.patch(`/admin/orgs/${quotaTarget.id}/usage-settings`, {
        vorgaengeQuota: quota,
        usageVisibility: visibilityValue,
      });
      setQuotaTarget(null);
      load();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Speichern");
    } finally {
      setSavingQuota(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/orgs/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Löschen");
      setDeleteTarget(null);
    }
  };

  return (
    <div className={styles.tabSection}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px" }}>
          <label className={styles.filterLabel ?? styles.checkLabel}>Neue Organisation</label>
          <input
            type="text"
            className={styles.inviteInput}
            placeholder="z.B. Marketing-Team"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
          {newName.trim() && (
            <div className={styles.tdMuted} style={{ fontSize: "var(--text-xs)", marginTop: "0.25rem" }}>
              Slug: {slugify(newName) || "—"}
            </div>
          )}
        </div>
        <Button onClick={handleCreate} loading={creating} size="sm" variant="secondary">
          <Plus size={14} /> Anlegen
        </Button>
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {loading ? (
        <div className={styles.loadingHint}>Wird geladen…</div>
      ) : orgs.length === 0 ? (
        <div className={styles.emptyHint}>Noch keine Organisationen.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Mitglieder</th>
                <th>Kontingent</th>
                <th>Verbrauch</th>
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td className={styles.tdName}>{o.name}</td>
                  <td className={styles.tdMuted}>{o.slug}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.orgMoreChip}
                      title="Mitglieder verwalten"
                      onClick={() => setManaging(o)}
                    >
                      <Users size={12} /> {o.memberCount}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.orgMoreChip}
                      title="Kontingent und Sichtbarkeit bearbeiten"
                      onClick={() => openQuota(o)}
                    >
                      {o.vorgaengeQuota === null
                        ? "kein Limit"
                        : `${o.vorgaengeQuota.toLocaleString("de-DE")} / Monat`}
                      {o.usageVisibility === "admins" ? " · nur Admins" : ""}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.orgMoreChip}
                      title="Verlauf nach Monat, Tag oder Stunde ansehen"
                      onClick={() => setVerbrauchTarget(o)}
                    >
                      {o.verbrauchLaufend.toLocaleString("de-DE")}
                      {o.vorgaengeQuota !== null && ` / ${o.vorgaengeQuota.toLocaleString("de-DE")}`}
                      {" "}im Monat
                    </button>
                  </td>
                  <td className={styles.tdMuted}>{formatDe(o.createdAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.25rem" }}>
                      <button
                        className={styles.deleteBtn}
                        title="Umbenennen"
                        onClick={() => { setRenaming(o); setRenameValue(o.name); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className={styles.deleteBtn}
                        title="Löschen"
                        onClick={() => setDeleteTarget(o)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {renaming && (
        <div className={styles.modalOverlay} onClick={() => setRenaming(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3>Organisation umbenennen</h3>
            <input
              type="text"
              className={styles.inviteInput}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
            />
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setRenaming(null)}>Abbrechen</Button>
              <Button variant="secondary" onClick={handleRename}>Speichern</Button>
            </div>
          </div>
        </div>
      )}

      {verbrauchTarget && (
        <VerbrauchModal
          orgId={verbrauchTarget.id}
          orgName={verbrauchTarget.name}
          onClose={() => setVerbrauchTarget(null)}
        />
      )}

      {quotaTarget && (
        <div className={styles.modalOverlay} onClick={() => setQuotaTarget(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3>Kontingent — {quotaTarget.name}</h3>
            <label className={styles.checkLabel} htmlFor="kontingent-vorgaenge">
              Bot-Vorgänge pro Monat
            </label>
            <input
              id="kontingent-vorgaenge"
              type="number"
              min={1}
              step={1}
              placeholder="leer = unbegrenzt"
              className={styles.inviteInput}
              value={quotaValue}
              onChange={(e) => setQuotaValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleSaveQuota()}
            />
            <p className={styles.tdMuted} style={{ fontSize: "0.8rem", margin: "0.25rem 0 0.75rem" }}>
              Bei Überschreitung arbeiten die Bots weiter; der Mehrverbrauch wird berechnet.
            </p>

            <label className={styles.checkLabel} htmlFor="kontingent-sichtbarkeit">
              Wer sieht den Verbrauch
            </label>
            <select
              id="kontingent-sichtbarkeit"
              className={styles.inviteInput}
              value={visibilityValue}
              onChange={(e) => setVisibilityValue(e.target.value as "members" | "admins")}
            >
              <option value="members">Alle Mitglieder der Organisation</option>
              <option value="admins">Nur Owner und Admins</option>
            </select>

            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setQuotaTarget(null)}>Abbrechen</Button>
              <Button variant="secondary" onClick={handleSaveQuota} disabled={savingQuota}>
                {savingQuota ? "Speichert…" : "Speichern"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3>Organisation löschen</h3>
            <p>
              <strong>{deleteTarget.name}</strong> wirklich löschen? Geht nur, wenn keine
              Mitglieder (außer Owner) mehr zugeordnet sind.
            </p>
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
              <Button variant="danger" onClick={handleDelete}>Löschen</Button>
            </div>
          </div>
        </div>
      )}

      {managing && (
        <OrgMembersModal
          org={managing}
          onClose={() => { setManaging(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Org members management modal ──

function OrgMembersModal({ org, onClose }: { org: AdminOrg; onClose: () => void }) {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("member");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<{ members: OrgMember[] }>(`/admin/orgs/${org.id}/members`),
      api.get<{ users: AdminUser[] }>("/admin/users"),
    ])
      .then(([m, u]) => {
        setMembers(m.members);
        setAllUsers(u.users.filter((x) => !x.isBot));
      })
      .catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => load(), [load]);

  const memberIds = new Set(members.map((m) => m.userId));
  const assignable = allUsers.filter((u) => !memberIds.has(u.id));

  const handleAdd = async () => {
    if (!addUserId) return;
    try {
      await api.post(`/admin/orgs/${org.id}/members`, { userId: addUserId, role: addRole });
      setAddUserId("");
      load();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Hinzufügen");
    }
  };

  const handleRole = async (userId: string, role: string) => {
    try {
      await api.patch(`/admin/orgs/${org.id}/members/${userId}`, { role });
      setMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, role } : m)));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Ändern");
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await api.delete(`/admin/orgs/${org.id}/members/${userId}`);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Entfernen");
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalBox} onClick={(e) => e.stopPropagation()} style={{ minWidth: "min(560px, 90vw)" }}>
        <h3>Mitglieder — {org.name}</h3>

        {error && <div className={styles.errorMsg}>{error}</div>}

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", margin: "0.75rem 0" }}>
          <div style={{ flex: "1 1 auto" }}>
            <label className={styles.filterLabel ?? styles.checkLabel}>Benutzer zuordnen</label>
            <select className={styles.inviteSelect} value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
              <option value="">— Benutzer wählen —</option>
              {assignable.map((u) => (
                <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>
              ))}
            </select>
          </div>
          <select className={styles.inviteSelect} value={addRole} onChange={(e) => setAddRole(e.target.value)}>
            <option value="member">Mitglied</option>
            <option value="moderator">Moderator</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
          <Button size="sm" variant="secondary" onClick={handleAdd} disabled={!addUserId}>
            <UserPlus size={14} /> Hinzufügen
          </Button>
        </div>

        {loading ? (
          <div className={styles.loadingHint}>Wird geladen…</div>
        ) : members.length === 0 ? (
          <div className={styles.emptyHint}>Keine Mitglieder.</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr><th>Name</th><th>Rolle</th><th></th></tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId}>
                    <td className={styles.tdName}>
                      {m.displayName}
                      <div className={styles.tdMuted} style={{ fontSize: "var(--text-xs)" }}>{m.email}</div>
                    </td>
                    <td>
                      <select
                        className={styles.roleSelect}
                        value={m.role}
                        onChange={(e) => handleRole(m.userId, e.target.value)}
                      >
                        <option value="member">{orgRoleLabelDe("member")}</option>
                        <option value="moderator">{orgRoleLabelDe("moderator")}</option>
                        <option value="admin">{orgRoleLabelDe("admin")}</option>
                        <option value="owner">{orgRoleLabelDe("owner")}</option>
                      </select>
                    </td>
                    <td>
                      <button className={styles.deleteBtn} title="Entfernen" onClick={() => handleRemove(m.userId)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.modalActions}>
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
        </div>
      </div>
    </div>
  );
}

// ── Invitations Tab ──

interface Org {
  id: string;
  name: string;
  slug: string;
}

function InvitationsTab() {
  const { user } = useAuth();
  // Owner darf in JEDE Organisation einladen (Server prüft keine Mitgliedschaft
  // bei explizitem orgId) — /orgs liefert aber nur die eigenen Mitglieds-Orgs,
  // dadurch fehlten z. B. Cinch Seal und G&N im Dropdown. Nicht-Owner-Admins
  // bekommen auf /admin/orgs ein 403, deshalb der Zweig statt Umschalten.
  const isOwner = user?.orgRole === "owner";
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>(""); // "" = auto, "__new__" = neue Org
  const [newOrgName, setNewOrgName] = useState("");

  useEffect(() => {
    Promise.all([
      api.get<{ invitations: Invitation[] }>("/auth/invitations"),
      api.get<{ organizations: Org[] }>(isOwner ? "/admin/orgs" : "/orgs"),
    ]).then(([invData, orgData]) => {
      setInvitations(invData.invitations);
      setOrgs(orgData.organizations);
      if (orgData.organizations.length > 0) {
        setSelectedOrgId(orgData.organizations[0].id);
      }
    }).catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [isOwner]);

  const handleCreate = async () => {
    setCreating(true);
    setError("");
    setSuccess("");
    try {
      let orgId = selectedOrgId;

      // Neue Org erstellen falls ausgewählt
      if (orgId === "__new__") {
        const trimmed = newOrgName.trim();
        if (!trimmed) {
          setError("Bitte einen Organisationsnamen eingeben.");
          setCreating(false);
          return;
        }
        const slug = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        if (slug.length < 3) {
          setError("Organisationsname zu kurz (mind. 3 Zeichen).");
          setCreating(false);
          return;
        }
        const orgData = await api.post<{ organization: Org }>("/orgs", { name: trimmed, slug });
        orgId = orgData.organization.id;
        setOrgs((prev) => [...prev, orgData.organization]);
        setSelectedOrgId(orgId);
        setNewOrgName("");
      }

      const body: { maxUses: number; expiresInDays: number; email?: string; orgId?: string } = { maxUses, expiresInDays };
      if (inviteEmail.trim()) body.email = inviteEmail.trim();
      if (orgId && orgId !== "__new__") body.orgId = orgId;
      const data = await api.post<{ invitation: Invitation }>("/auth/invitations", body);
      setInvitations((prev) => [data.invitation, ...prev]);
      setInviteEmail("");
      await navigator.clipboard.writeText(data.invitation.link).catch(() => {});
      setSuccess("Einladung erstellt und Link kopiert!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await api.delete(`/auth/invitations/${id}`);
      setInvitations((prev) =>
        prev.map((inv) =>
          inv.id === id ? { ...inv, revokedAt: new Date().toISOString(), isActive: false } : inv,
        ),
      );
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Widerrufen");
    }
  };

  const handleCopy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setSuccess("Link kopiert!");
      setTimeout(() => setSuccess(""), 2000);
    } catch {
      setError("Link konnte nicht kopiert werden");
    }
  };

  return (
    <div className={styles.tabSection}>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-sm)", margin: 0 }}>
          Eingeladene Benutzer werden automatisch der gewählten Organisation zugewiesen.
        </p>
        {/* Shared inline style for uniform field height */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label className={styles.filterLabel ?? styles.checkLabel}>E-Mail (optional)</label>
            <input
              type="email"
              className={styles.inviteInput}
              placeholder="einladung@beispiel.de"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
          <div style={{ flex: "1 1 180px" }}>
            <label className={styles.filterLabel ?? styles.checkLabel}>Organisation</label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className={styles.inviteSelect}
            >
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
              <option value="__new__">+ Neue Organisation</option>
            </select>
          </div>
          {selectedOrgId === "__new__" && (
            <div style={{ flex: "1 1 180px" }}>
              <label className={styles.filterLabel ?? styles.checkLabel}>Name der neuen Org</label>
              <input
                type="text"
                className={styles.inviteInput}
                placeholder="z.B. Marketing-Team"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
              />
            </div>
          )}
          <div style={{ flex: "0 0 80px" }}>
            <label className={styles.filterLabel ?? styles.checkLabel}>Nutzungen</label>
            <select
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
              className={styles.inviteSelect}
            >
              {[1, 5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>{n}×</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "0 0 100px" }}>
            <label className={styles.filterLabel ?? styles.checkLabel}>Gültig für</label>
            <select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className={styles.inviteSelect}
            >
              <option value={1}>1 Tag</option>
              <option value={3}>3 Tage</option>
              <option value={7}>7 Tage</option>
              <option value={14}>14 Tage</option>
              <option value={30}>30 Tage</option>
            </select>
          </div>
          <Button onClick={handleCreate} loading={creating} size="sm" variant="secondary">
            <UserPlus size={14} /> Einladung erstellen
          </Button>
        </div>
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}
      {success && <div style={{ color: "#22C55E", fontSize: "var(--text-sm)", marginBottom: "0.5rem" }}>{success}</div>}

      {loading ? (
        <div className={styles.loadingHint}>Wird geladen…</div>
      ) : invitations.length === 0 ? (
        <div className={styles.emptyHint}>Noch keine Einladungen erstellt.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Empfänger</th>
                <th>Nutzungen</th>
                <th>Gültig bis</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id} style={{ opacity: inv.isActive ? 1 : 0.5 }}>
                  <td className={styles.tdName}>{inv.email ?? "Allgemein"}</td>
                  <td className={styles.tdMuted}>{inv.useCount}/{inv.maxUses}</td>
                  <td className={styles.tdMuted}>{new Date(inv.expiresAt).toLocaleDateString("de-DE")}</td>
                  <td>
                    <span className={`${styles.badge} ${inv.isActive ? styles.statusActive : styles.statusInactive}`}>
                      {inv.revokedAt ? "Widerrufen" : inv.isActive ? "Aktiv" : "Abgelaufen"}
                    </span>
                  </td>
                  <td className={styles.tdMuted}>{formatDe(inv.createdAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.25rem" }}>
                      {inv.isActive && (
                        <button
                          onClick={() => handleCopy(inv.link)}
                          title="Link kopieren"
                          style={{ padding: "0.4rem", borderRadius: "var(--radius-md)", color: "var(--color-text-secondary)", background: "transparent", cursor: "pointer", border: "none" }}
                        >
                          <Copy size={14} />
                        </button>
                      )}
                      {inv.isActive && (
                        <button
                          onClick={() => handleRevoke(inv.id)}
                          title="Widerrufen"
                          style={{ padding: "0.4rem", borderRadius: "var(--radius-md)", color: "var(--color-error)", background: "transparent", cursor: "pointer", border: "none" }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Audit Tab ──

function AuditTab() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [actions, setActions] = useState<string[]>([]);
  const [filterAction, setFilterAction] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(1);

  // Load distinct actions once
  useEffect(() => {
    api
      .get<{ actions: string[] }>("/admin/audit-log/actions")
      .then((d) => setActions(d.actions))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", "50");
    if (filterAction) params.set("action", filterAction);
    if (filterFrom) params.set("from", filterFrom);
    if (filterTo) {
      // Include the full day (to end-of-day)
      const to = new Date(filterTo);
      to.setHours(23, 59, 59, 999);
      params.set("to", to.toISOString());
    }
    api
      .get<{ entries: AuditEntry[]; pagination: Pagination }>(`/admin/audit-log?${params.toString()}`)
      .then((d) => {
        setEntries(d.entries);
        setPagination(d.pagination);
      })
      .catch((err: { message?: string }) => setError(err.message ?? "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [page, filterAction, filterFrom, filterTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterApply = () => {
    setPage(1);
    load();
  };

  const formatDetails = (details: Record<string, unknown> | null): string => {
    if (!details) return "—";
    const str = JSON.stringify(details);
    return str.length > 80 ? str.slice(0, 80) + "…" : str;
  };

  return (
    <div className={styles.tabSection}>
      <div className={styles.filterRow}>
        <select
          className={styles.selectFilter}
          value={filterAction}
          onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
        >
          <option value="">Alle Aktionen</option>
          {actions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        <div className={styles.dateFilter}>
          <label className={styles.dateLabel}>Von</label>
          <input
            type="date"
            className={styles.dateInput}
            value={filterFrom}
            onChange={(e) => { setFilterFrom(e.target.value); setPage(1); }}
          />
        </div>

        <div className={styles.dateFilter}>
          <label className={styles.dateLabel}>Bis</label>
          <input
            type="date"
            className={styles.dateInput}
            value={filterTo}
            onChange={(e) => { setFilterTo(e.target.value); setPage(1); }}
          />
        </div>

        <Button size="sm" variant="secondary" onClick={handleFilterApply}>
          Filtern
        </Button>

        {(filterAction || filterFrom || filterTo) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilterAction("");
              setFilterFrom("");
              setFilterTo("");
              setPage(1);
            }}
          >
            Zurücksetzen
          </Button>
        )}
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {loading ? (
        <div className={styles.loadingHint}>Wird geladen…</div>
      ) : entries.length === 0 ? (
        <div className={styles.emptyHint}>Keine Einträge gefunden.</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Zeitstempel</th>
                  <th>Benutzer</th>
                  <th>Aktion</th>
                  <th>IP</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className={styles.tdMono}>{formatDe(e.createdAt)}</td>
                    <td className={styles.tdName}>{e.displayName ?? <span className={styles.tdMuted}>—</span>}</td>
                    <td>
                      <span className={`${styles.badge} ${styles.actionBadge}`}>{e.action}</span>
                    </td>
                    <td className={styles.tdMono}>{e.ip ?? "—"}</td>
                    <td className={styles.tdDetails} title={e.details ? JSON.stringify(e.details) : undefined}>
                      {formatDetails(e.details)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className={styles.pagination}>
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft size={14} />
              </Button>
              <span className={styles.pageInfo}>
                Seite {pagination.page} von {pagination.pages} ({pagination.total.toLocaleString("de-DE")} Einträge)
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
